import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
  Database, ShieldCheck, ShieldAlert, Loader2, CheckCircle2, XCircle, Lock,
  Pause, Play, RotateCcw, ClipboardCheck,
} from "lucide-react";

const CONFIRM_PHRASE = "RUN V5.1 PRODUCTION CUTOVER";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const V51Cutover = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [err, setErr] = useState("");
  const [verify, setVerify] = useState(null);
  const loopRef = useRef(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/cutover/status");
      setSt(r.data);
      return r.data;
    } catch { return null; }
  };

  const loadVerify = async () => {
    try { setVerify((await api.get("/admin/cutover/verify")).data); }
    catch { /* panel stays hidden */ }
  };

  const stepLoop = async (runId) => {
    if (loopRef.current || !runId) return;
    loopRef.current = true;
    setErr("");
    while (loopRef.current) {
      try {
        const r = await api.post("/admin/cutover/step", { run_id: runId });
        setSt((p) => ({ ...(p || {}), ...r.data }));
        if (r.data.done) break;
      } catch (e) {
        const d = e.response?.data?.detail || "";
        if (d === "step_in_flight") { await sleep(3000); continue; }
        if (d !== "paused") setErr(d || "Step failed — progress is checkpointed, safe to Resume.");
        break;
      }
    }
    loopRef.current = false;
    const d = await load();
    if (d?.status === "completed") loadVerify();
  };

  useEffect(() => {
    load().then((d) => {
      if (!d) return;
      if (d.status === "stepping" || d.status === "pf_stepping") stepLoop(d.run_id);
      if (d.status === "completed") loadVerify();
    });
    return () => { loopRef.current = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runPreflight = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/cutover/preflight");
      await load();
      stepLoop(r.data.run_id);
    } catch (e) { setErr(e.response?.data?.detail || "Failed to start preflight"); }
    setBusy(false);
  };

  const resolveOrphans = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/admin/cutover/resolve-orphans");
      await runPreflight();
    } catch (e) { setErr(e.response?.data?.detail || "Orphan resolution failed"); }
    setBusy(false);
  };

  const runCutover = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/cutover/execute", { confirm: phrase });
      setConfirming(false); setPhrase("");
      await load();
      stepLoop(r.data.run_id);
    } catch (e) { setErr(e.response?.data?.detail || "Cutover failed to start"); }
    setBusy(false);
  };

  const doPause = async () => {
    loopRef.current = false;
    try { await api.post("/admin/cutover/pause"); } catch { /* already stopped */ }
    await load();
  };

  const doResume = async () => {
    setErr("");
    try {
      const r = await api.post("/admin/cutover/resume");
      await load();
      stepLoop(r.data.run_id);
    } catch (e) { setErr(e.response?.data?.detail || "Resume failed"); }
  };

  const doReset = async () => {
    setBusy(true); setErr("");
    try { await api.post("/admin/cutover/reset-stuck", { reason: "admin reset via dashboard" }); await load(); }
    catch (e) { setErr(e.response?.data?.detail || "Reset refused"); }
    setBusy(false);
  };

  if (!st) return null;
  const pf = st.preflight;
  const done = st.status === "completed";
  const stepping = st.status === "stepping";
  const pfStepping = st.status === "pf_stepping";
  const paused = st.status === "paused";
  const interrupted = st.status === "interrupted" || (st.status === "running" && st.job_alive === false);
  const active = stepping || pfStepping || loopRef.current;
  const canCutover = st.status === "preflight_passed" && pf?.all_pass &&
    (pf?.orphan_details?.length || 0) === 0 && !active && !done;

  return (
    <div data-testid="v51-cutover-section" className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0B2A6B]/10">
          <Database className="h-5 w-5 text-[#0B2A6B]" />
        </span>
        <div>
          <h2 className="font-display text-lg font-bold text-slate-900">V5.1 Production Cutover</h2>
          <p className="text-xs text-slate-500">
            Request-driven stepper — each batch runs inside a short admin request and checkpoints to the database.
            Current live count: <b data-testid="live-count">{st.live_count?.toLocaleString()}</b>
          </p>
        </div>
      </div>

      {done && (
        <div data-testid="cutover-completed" className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-4 w-4" /> Migration completed — cutover permanently disabled.</p>
          <p className="mt-1">Completed: {st.completed_at} · Backup: <code>{st.backup_collection}</code> ({st.backup_count?.toLocaleString()} records) · Final count: <b>{st.final_count?.toLocaleString()}</b></p>
        </div>
      )}

      {done && (
        <div data-testid="verification-panel" className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <ClipboardCheck className="h-4 w-4 text-[#0B2A6B]" /> Live Verification Panel
            </p>
            <button data-testid="rerun-verification-btn" onClick={loadVerify}
              className="rounded-lg border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-white">
              Re-run verification
            </button>
          </div>
          {!verify && <p className="text-xs text-slate-500">Running live checks…</p>}
          {verify && (
            <>
              {verify.checks.map((c) => (
                <div key={c.name} data-testid={`verify-${c.name}`} className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs">
                  <span className="flex items-center gap-2 font-medium text-slate-700">
                    {c.pass ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                    {c.name.replaceAll("_", " ")}
                  </span>
                  <span className="text-slate-500">{c.detail}</span>
                </div>
              ))}
              <p className={`text-xs font-semibold ${verify.all_pass ? "text-emerald-700" : "text-red-600"}`}>
                {verify.all_pass ? "All live-verification checks passed." : "Verification found issues — investigate before proceeding."}
                <span className="ml-2 font-normal text-slate-400">(captured {verify.captured_at})</span>
              </p>
            </>
          )}
        </div>
      )}

      {(stepping || pfStepping || paused) && !done && (
        <div data-testid="stepper-panel" className={`space-y-2 rounded-xl border p-3 text-sm ${paused ? "border-amber-200 bg-amber-50" : "border-blue-200 bg-blue-50"}`}>
          <p className={`flex items-center gap-2 font-semibold ${paused ? "text-amber-800" : "text-blue-800"}`}>
            {paused ? <Pause className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
            {paused ? "Paused at checkpoint" : pfStepping ? `Preflight running (${st.pf_step || 1}/3)` : `Cutover stepping (step ${st.step || 1}/8)`}
            <span className="font-normal">— {st.progress}</span>
          </p>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/70">
            <div data-testid="stepper-progress-bar" className={`h-full rounded-full transition-all ${paused ? "bg-amber-500" : "bg-blue-600"}`} style={{ width: `${st.pct || 3}%` }} />
          </div>
          <div className="flex items-center gap-2">
            {stepping && (
              <button data-testid="pause-btn" onClick={doPause}
                className="inline-flex items-center gap-1 rounded-lg border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-100">
                <Pause className="h-3.5 w-3.5" /> Pause
              </button>
            )}
            {paused && (
              <button data-testid="resume-btn" onClick={doResume}
                className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">
                <Play className="h-3.5 w-3.5" /> Resume from checkpoint
              </button>
            )}
            {stepping && !loopRef.current && (
              <button data-testid="continue-btn" onClick={() => stepLoop(st.run_id)}
                className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">
                <Play className="h-3.5 w-3.5" /> Continue stepping
              </button>
            )}
            <span className={`text-[11px] ${paused ? "text-amber-600" : "text-blue-600"}`}>
              Progress is checkpointed in the database — refresh or connection loss never restarts the migration.
            </span>
          </div>
        </div>
      )}

      {interrupted && !done && (
        <div data-testid="interrupted-panel" className="space-y-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <p className="flex items-center gap-2 font-semibold"><ShieldAlert className="h-4 w-4" /> Stuck migration state detected</p>
          <p className="text-xs">{st.progress}</p>
          <button data-testid="reset-stuck-btn" onClick={doReset} disabled={busy}
            className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50">
            <RotateCcw className="h-3.5 w-3.5" /> Reset Stuck State (verified safe — audited)
          </button>
          <p className="text-[11px] text-red-600">Reset is refused unless the live dataset is provably untouched (1,718 legacy records, zero migrated docs, no swap).</p>
        </div>
      )}

      {st.status === "failed" && !active && (
        <p data-testid="failed-banner" className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">{st.progress} — database auto-rolled back. Re-run Preflight.</p>
      )}

      {pf && !done && !pfStepping && (
        <div data-testid="preflight-gates" className="space-y-1.5">
          {pf.gates.map((g) => (
            <div key={g.name} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-xs">
              <span className="flex items-center gap-2 font-medium text-slate-700">
                {g.pass ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <XCircle className="h-3.5 w-3.5 text-red-500" />}
                {g.name.replaceAll("_", " ")}
              </span>
              <span className="text-slate-500">{g.detail}</span>
            </div>
          ))}
          <p className={`text-xs font-semibold ${pf.all_pass ? "text-emerald-700" : "text-red-600"}`}>
            {pf.all_pass ? "All gates passed — cutover unlocked." : pf.already_migrated ? "Already migrated." : "Gates failed — cutover locked."}
            <span className="ml-2 font-normal text-slate-400">(preflight completed {pf.ran_at})</span>
          </p>

          {pf.orphan_details?.length > 0 && (
            <div data-testid="orphan-details" className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-xs font-semibold text-amber-800">Orphan reconciliation table ({pf.orphan_details.length}) — no lead is modified or deleted:</p>
              <div className="max-h-48 overflow-auto">
                <table className="w-full text-[11px]">
                  <thead><tr className="text-left text-amber-700"><th className="pr-2">Collection</th><th className="pr-2">Lead doc IDs</th><th className="pr-2">Field</th><th className="pr-2">Ref</th><th className="pr-2">Found in</th><th className="pr-2">Class</th><th>Proposed resolution</th></tr></thead>
                  <tbody>
                    {pf.orphan_details.map((o) => (
                      <tr key={o.referenced_college_id} className="border-t border-amber-100 align-top text-amber-900">
                        <td className="pr-2">{o.collection}</td>
                        <td className="pr-2 font-mono">{o.documentIds?.join(", ")}</td>
                        <td className="pr-2">{o.field}</td>
                        <td className="pr-2 font-mono">{o.referenced_college_id || "(blank)"}</td>
                        <td className="pr-2">{o.found_in || "nowhere"}</td>
                        <td className="pr-2">{o.classification}</td>
                        <td>{o.proposed_resolution}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button data-testid="resolve-orphans-btn" onClick={resolveOrphans} disabled={busy || active}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
                Resolve Orphans (audited — reruns preflight automatically)
              </button>
            </div>
          )}
        </div>
      )}

      {err && <p data-testid="cutover-error" className="text-xs font-medium text-red-600">{String(err)}</p>}

      {!done && !active && !paused && !interrupted && (
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="run-preflight-btn" onClick={runPreflight} disabled={busy}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0B2A6B] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#081E4D] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Run Preflight
          </button>
          <button data-testid="run-cutover-btn" onClick={() => setConfirming(true)} disabled={!canCutover || busy}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40">
            <Lock className="h-4 w-4" /> Run Production Cutover
          </button>
        </div>
      )}

      {confirming && (
        <div data-testid="cutover-confirm-dialog" className="space-y-3 rounded-xl border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-semibold text-red-800">⚠️ This replaces the live college dataset (a verified backup is created first). Type the phrase to confirm:</p>
          <p className="text-xs font-mono text-red-700">{CONFIRM_PHRASE}</p>
          <input data-testid="cutover-confirm-input" value={phrase} onChange={(e) => setPhrase(e.target.value)}
            className="w-full rounded-lg border border-red-300 px-3 py-2 text-sm" placeholder="Type confirmation phrase" />
          <div className="flex gap-2">
            <button data-testid="cutover-confirm-execute" onClick={runCutover} disabled={phrase !== CONFIRM_PHRASE || busy}
              className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">Execute Cutover</button>
            <button onClick={() => { setConfirming(false); setPhrase(""); }} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};
